import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { GridColumnsPanel } from '@mui/x-data-grid';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index ? <Box sx={{ pt: 1 }}>{children}</Box> : null}
    </div>
  );
}

function ColumnOrderSection({ columnDefs, fields, setFields }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  /** Same string across renders unless field ids/order change (avoids reset on new columnDefs array identity). */
  const columnFieldOrderSignature = columnDefs?.length ? columnDefs.map((c) => c.field).join('|') : '';

  useEffect(() => {
    if (!columnDefs?.length) return;
    setFields(columnDefs.map((c) => c.field));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- columnDefs read when signature changes; omit reference identity
  }, [columnFieldOrderSignature, setFields]);

  const labelFor = (field) => {
    const c = columnDefs.find((x) => x.field === field);
    return c?.headerName || field;
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('text/plain'), 10);
    setDragOverIndex(null);
    setDragIndex(null);
    if (Number.isNaN(from) || from === dropIndex) return;

    setFields((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(from, 1);
      copy.splice(dropIndex, 0, item);
      return copy;
    });
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag a row by the handle and drop where this column should appear. Click <strong>Apply order</strong> below to
        save.
      </Typography>
      <List dense disablePadding>
        {fields.map((field, index) => (
          <ListItem
            key={field}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              cursor: 'grab',
              borderBottom: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              mb: 0.25,
              bgcolor:
                dragOverIndex === index ? 'action.selected' : dragIndex === index ? 'action.hover' : 'transparent',
              opacity: dragIndex === index ? 0.85 : 1,
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
              <DragIndicatorIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={labelFor(field)} secondary={field} />
          </ListItem>
        ))}
      </List>
    </>
  );
}

/**
 * Combined columns visibility (MIT panel) + column order for list applets.
 * Must render inside DataGrid toolbar slot so useGridApiContext works.
 */
export function DataGridColumnSelectorOrderButton({
  showVisibility = true,
  columnOrderStorageKey,
  orderedColumns,
  onSaveColumnOrder,
  onResetColumnOrder,
}) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [orderFields, setOrderFields] = useState([]);

  const hasOrder = Boolean(columnOrderStorageKey);
  const showTabs = showVisibility && hasOrder;

  useEffect(() => {
    if (open && orderedColumns?.length && hasOrder) {
      setOrderFields(orderedColumns.map((c) => c.field));
    }
  }, [open, orderedColumns, hasOrder]);

  const handleClose = () => setOpen(false);

  const handleApplyOrder = () => {
    onSaveColumnOrder?.(orderFields);
    handleClose();
  };

  const handleResetOrder = () => {
    onResetColumnOrder?.();
    handleClose();
  };

  if (!showVisibility && !hasOrder) return null;

  const c1 = theme.palette.primary.main;
  const c2 = theme.palette.secondary?.main || theme.palette.primary.dark;
  const grad = `linear-gradient(135deg, ${alpha(c1, theme.palette.mode === 'dark' ? 0.42 : 0.22)}, ${alpha(c2, theme.palette.mode === 'dark' ? 0.38 : 0.28)})`;

  return (
    <>
      <Button
        size="small"
        variant="contained"
        startIcon={<ViewColumnIcon />}
        onClick={() => {
          setTab(0);
          setOpen(true);
        }}
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 700,
          borderRadius: 2,
          px: 1.35,
          py: 0.65,
          textTransform: 'none',
          color: theme.palette.getContrastText(c1),
          background: grad,
          border: '2px solid',
          borderColor: alpha(c1, 0.85),
          boxShadow: `0 2px 10px ${alpha(c1, 0.35)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(c1, 0.92)}, ${alpha(c2, 0.85)})`,
            borderColor: alpha(c1, 1),
            boxShadow: `0 4px 14px ${alpha(c1, 0.45)}`,
          },
        }}
      >
        Column selector / order
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle
          sx={{
            py: 1.75,
            fontWeight: 700,
            background: grad,
            color: theme.palette.getContrastText(c1),
            borderBottom: '3px solid',
            borderColor: alpha(c1, 0.9),
          }}
        >
          Column selector / order
        </DialogTitle>
        <DialogContent dividers>
          {showTabs ? (
            <>
              <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tab label="Selector" />
                <Tab label="Order" />
              </Tabs>
              <TabPanel value={tab} index={0}>
                <Box sx={{ maxHeight: 'min(52vh, 420px)', overflow: 'auto', mx: -1 }}>
                  <GridColumnsPanel autoFocusSearchField={false} />
                </Box>
              </TabPanel>
              <TabPanel value={tab} index={1}>
                <ColumnOrderSection
                  columnDefs={orderedColumns}
                  fields={orderFields}
                  setFields={setOrderFields}
                />
              </TabPanel>
            </>
          ) : showVisibility ? (
            <Box sx={{ maxHeight: 'min(52vh, 420px)', overflow: 'auto', mx: -1 }}>
              <GridColumnsPanel autoFocusSearchField={false} />
            </Box>
          ) : (
            <ColumnOrderSection
              columnDefs={orderedColumns}
              fields={orderFields}
              setFields={setOrderFields}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          {hasOrder && (showTabs ? tab === 1 : !showVisibility) ? (
            <>
              <Button onClick={handleResetOrder} color="inherit">
                Reset column order
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" onClick={handleApplyOrder}>
                Apply order
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
